import clsx from "clsx";
import React, { useEffect, useState } from "react";
import { isInRange } from "../../../helpers/mathHelper";
import { useVisibility } from "../../../hooks/useVisibility";
import { ChangeAmountButton, Mode, sizes } from "./ChangeAmountButton";
import { ChangeAmountPopover } from "./ChangeAmountPopover";

interface QuantitySpinnerProps {
    value: number;
    min?: number;
    max?: number;
    total?: number;
    classNames?: { fieldset?: string; button?: string; label?: string };
    disabled?: boolean;
    size?: keyof typeof sizes;
    popoverParentId?: string;
    text?: string;
    minTooltipText?: string;
    maxTooltipText?: string;
    isLoading?: boolean;
    shouldSyncValue?: boolean;
    onChange: (value: number) => void;
    lockAtMax?: boolean;
}

// TODO: Move to ui-kit once the design and features are finalized.
/**
 * @param min Minimum possible value for this item.
 * @param max The max value possible for all the quantity spinners.
 * @param total Total is the number of every countable item in the booking. For example sum of all demographics.
 * @param shouldSyncValue For the purchase waitlist, mist purchase, and split pay flows. This flag will turn off sync, and will use outerValue instead.
 */
export const QuantitySpinner = ({
    value: outerValue,
    min = 0,
    max = Number.POSITIVE_INFINITY,
    total = 0,
    classNames,
    disabled,
    size,
    popoverParentId,
    text = "",
    minTooltipText,
    maxTooltipText,
    isLoading = false,
    shouldSyncValue = true,
    onChange,
    lockAtMax = true,
    ...rest
}: QuantitySpinnerProps) => {
    const [value, setValue] = useState(outerValue);
    const [manualCounterValue, setManualCounterValue] = useState(value);
    const [isManualError, setIsManualError] = useState(false);
    const [isPopoverVisible, { toggle: togglePopover }] = useVisibility(false);

    const isDecreaseDisabled = value <= min || !!disabled;
    const isIncreaseDisabled = (lockAtMax && (value >= max || (total && total >= max))) || !!disabled;
    const isError = total > max;

    const shouldSync = shouldSyncValue && outerValue !== value;

    useEffect(() => {
        /** Do not set anything if value being manually edited inside popover. */
        if (isPopoverVisible) {
            return;
        }

        /**
         * Sync value only if it's not consistent and not being manually edited by popover,
         * the use case - when quantity spinner hooked to the API call, in cases with slow connection,
         * API response may overwrite the local state resulting in quantity less than user currently set.
         */
        if (shouldSync) {
            onChange(value);
            /**
             * Purchase with waitlist case - we can change purchase demographics in the waitlist modal,
             * and we need to update quantity spinner on the make a purchase page with changes made from spinner in the waitlist modal.
             * For the split pay case we need don't need to sync since we need to reset spinner on the next guest payment.
             */
        } else if (!shouldSyncValue) {
            setValue(outerValue);
            setManualCounterValue(outerValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldSync, isPopoverVisible, value, onChange]);

    const handleValueChange = (value: number) => {
        setManualCounterValue(value);
        setValue(value);
        onChange(value);
    };

    const handleCounterBlur = () => {
        if (isManualError) {
            setValue(manualCounterValue);
        } else {
            setManualCounterValue(value);
            onChange(value);
        }

        togglePopover();
        setIsManualError(false);
    };

    const handleCounterManualChange = ({ target }: any) => {
        const rawInput = target.value;
        /** In case the input is empty setting the available min value. */
        if (rawInput.length === 0) {
            setValue(min);
            setIsManualError(false);
            return;
        }

        const newValue = Number.parseInt(rawInput, 10);
        /** In case the `newValue` is NaN setting the last valid value. */
        if (Number.isNaN(newValue)) {
            setValue(manualCounterValue);
            setIsManualError(false);
            return;
        }

        /**
         * In case the total is passed to the component calculate it, otherwise, use the new value.
         * The `manualCounterValue` holds the previous value, so total minus old plus new.
         */
        const newTotal = total === 0 ? newValue : total - manualCounterValue + newValue;

        setValue(newValue);

        if (isInRange({ value: newTotal, min, max })) {
            setIsManualError(false);
        } else {
            setIsManualError(true);
        }
    };

    return (
        <fieldset
            className={clsx("flex items-center", classNames?.fieldset)}
            disabled={disabled ?? isLoading}
            data-testid="quantity-spinner"
            {...rest}
        >
            <ChangeAmountButton
                value={value}
                minMax={min}
                disabled={isDecreaseDisabled}
                variant={Mode.DECREASE}
                className={classNames?.button}
                error={isError}
                size={size}
                isTooltipDisabled={disabled}
                minTooltipText={minTooltipText}
                onChange={handleValueChange}
            />

            <ChangeAmountPopover
                value={value}
                manualCounterValue={manualCounterValue}
                max={max}
                min={min}
                total={total}
                isOpen={isPopoverVisible}
                popoverParentId={popoverParentId}
                className={classNames?.label}
                isError={isError}
                isManualError={isManualError}
                text={text}
                onClick={togglePopover}
                onSubmit={handleCounterBlur}
                onChange={handleCounterManualChange}
            />

            <ChangeAmountButton
                value={value}
                minMax={max}
                disabled={isIncreaseDisabled}
                variant={Mode.INCREASE}
                className={classNames?.button}
                error={isError}
                lockAtMax={false}
                size={size}
                isTooltipDisabled={disabled}
                maxTooltipText={maxTooltipText}
                onChange={handleValueChange}
            />
        </fieldset>
    );
};
